import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from 'store';

import {
  FormField,
  IUser,
  LocationFlowType,
  LocationInterface,
  MemberData,
  RefCodeResponse,
  SentType
} from 'types';

export type UserStore = {
  isAuthorized: boolean;
  isLoading: boolean;
  errorMsg: string;
  fieldErrors: Record<string, string>;
  user: IUser | null;
  authStep: number;
  userData: MemberData | null;
  isMultipleRecordsError: boolean;
  communicationType: SentType;
  userCampaigns: FormField[];
  userPronouns: FormField[];
  refCodeData: RefCodeResponse | null;
  isWelcomePage: boolean;
  redirectFlow: LocationFlowType | null;
  selectedLocation: LocationInterface | null;
  previewPhoto: string | null;
};

const initialState: UserStore = {
  errorMsg: '',
  user: null,
  isLoading: false,
  fieldErrors: {},
  isAuthorized: false,
  authStep: 0,
  userData: null,
  isMultipleRecordsError: false,
  communicationType: 'sms',
  userCampaigns: [],
  userPronouns: [],
  refCodeData: null,
  isWelcomePage: false,
  redirectFlow: null,
  selectedLocation: null,
  previewPhoto: null
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.errorMsg = action.payload;
    },
    setRedirectFlow: (
      state,
      action: PayloadAction<LocationFlowType | null>
    ) => {
      state.redirectFlow = action.payload;
    },
    setFieldError: (
      state,
      action: PayloadAction<{ field: string; error: string }>
    ) => {
      state.fieldErrors[action.payload.field] = action.payload.error;
    },
    clearErrors: (state) => {
      state.errorMsg = '';
      state.fieldErrors = {};
    },
    setUser: (state, action: PayloadAction<IUser | null>) => {
      state.user = action.payload;
    },
    setIsAuthorized: (state, action: PayloadAction<boolean>) => {
      state.isAuthorized = action.payload;
    },
    authSessionFinished: (state) => {
      state.isAuthorized = false;
      state.user = null;
    },
    goNextStep: (state: UserStore) => {
      state.authStep += 1;
    },
    goPrevStep: (state: UserStore) => {
      state.authStep -= 1;
    },
    goToStep: (state: UserStore, action: PayloadAction<number>) => {
      state.authStep = action.payload;
    },
    setUserData: (
      state: UserStore,
      action: PayloadAction<MemberData | null>
    ) => {
      state.userData = action.payload;
    },
    setMultipleRecords: (state: UserStore, action: PayloadAction<boolean>) => {
      state.isMultipleRecordsError = action.payload;
    },
    setCommunicationType: (
      state: UserStore,
      action: PayloadAction<SentType>
    ) => {
      state.communicationType = action.payload;
    },
    setUserCampaigns: (
      state: UserStore,
      action: PayloadAction<FormField[]>
    ) => {
      state.userCampaigns = action.payload;
    },
    setUserPronouns: (state: UserStore, action: PayloadAction<FormField[]>) => {
      state.userPronouns = action.payload;
    },
    setRefCodeData: (
      state: UserStore,
      action: PayloadAction<RefCodeResponse | null>
    ) => {
      state.refCodeData = action.payload;
    },
    clearRefCodeData: (state: UserStore) => {
      state.refCodeData = null;
    },
    setShowWelcomePage: (state: UserStore, action: PayloadAction<boolean>) => {
      state.isWelcomePage = action.payload;
    },
    selectLocation: (
      state,
      action: PayloadAction<LocationInterface | null>
    ) => {
      state.selectedLocation = action.payload;
    },
    setPreviewPhoto: (
      state: UserStore,
      action: PayloadAction<string | null>
    ) => {
      state.previewPhoto = action.payload;
    }
  }
});

export const {
  setError,
  setIsLoading,
  setIsAuthorized,
  setUser,
  setFieldError,
  authSessionFinished,
  clearErrors,
  goNextStep,
  goPrevStep,
  goToStep,
  setUserData,
  setMultipleRecords,
  setCommunicationType,
  setUserCampaigns,
  setUserPronouns,
  setRefCodeData,
  setShowWelcomePage,
  clearRefCodeData,
  setRedirectFlow,
  selectLocation,
  setPreviewPhoto
} = userSlice.actions;

export const userSelector = (state: RootState) => state.user;

export default userSlice.reducer;
